// src/App.js
import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import CookieConsent from "react-cookie-consent";
import mixpanel from "mixpanel-browser";

import "./App.css";
import "./css/normalize.css";
import "./css/okgather1.webflow.css";
import "./css/webflow.css";
import { getTrafficSource, captureLandingPage, captureTrafficSource } from './utils/sourceTracking';

import CreateAccount from "./components/CreateAccount";
import Login from "./components/Login";
import GatheringMatcher from "./components/GatheringMatcher";
import MyEvents from "./components/MyEvents";
import Connections from "./components/Connections";
import ConnectViaLink from "./components/ConnectViaLink";
import Circles from "./components/Circles";
import JoinEvent from "./components/JoinEvent";
import StayInTouch from "./components/StayInTouch";
import Wishlist from "./components/Wishlist";
import PublicWishlist from "./components/PublicWishlist";
import EditProfile from "./components/EditProfile";
import Home from "./components/Home";
import Slots from "./components/Slots";
import Checkout from "./components/Checkout";
import EventSeries from "./components/EventSeries";
import FindGathering from "./components/FindGathering";
import AdminPanel from "./components/admin_panel/AdminPanel.js";
import AdminLogin from "./components/admin_panel/AdminLogin.js";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Faq from "./components/Faq";
import WaitingListConfirmation from "./components/WaitingListConfirmation";
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import BlogAdmin from './components/admin_panel/BlogAdmin';
import ThemeLandingPage from './components/ThemeLandingPage';
import CategoriesPage from './components/CategoriesPage';
import ReferralDashboard from './components/ReferralDashboard';
import ReferralTerms from './components/ReferralTerms';
import ReferralLanding from './components/ReferralLanding';
import SignupRedirect from './components/SignupRedirect';
import NotFound from './components/NotFound';
import ScrollToTop from './components/ScrollToTop';
import SocialPage from './components/SocialPage';
import LocalEventsPage from './components/LocalEventsPage';
import LocalEventsIndex from './components/LocalEventsIndex';
import SubscriberLayout from './components/SubscriberLayout';

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || "e174bc50dcad4ccb9d3c72065503888b";
const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID || "644800014937817";
const AW_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "AW-17003589519";

// Global NCC parameter capture - runs on EVERY page load
function NCCParamCapture() {
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const campaignParam = urlParams.get('campaign');
    if (campaignParam) {
      localStorage.setItem('ncc_campaign', campaignParam);
    }
    // Clear any stale ncc flag — NCC trials are no longer active.
    localStorage.removeItem('ncc');
  }, [location.search]);

  return null;
}

// Captures acquisition params on EVERY page load and persists them so they're
// still available once the user reaches checkout:
//   ?src=       the specific Meetup/Eventbrite group identifier
//   ?platform=  explicit platform override (e.g. "meetup", "eventbrite") — falls
//               back to referrer-based detection (getTrafficSource) if omitted
//   ?placement= where on that platform the link was placed, e.g. "event_description",
//               "comment", "group_description"
function AcquisitionParamsCapture() {
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    const srcParam = urlParams.get('src');
    if (srcParam) {
      localStorage.setItem('source_group', srcParam.trim());
    }

    const platformParam = urlParams.get('platform');
    if (platformParam) {
      localStorage.setItem('source_platform', platformParam.trim());
    }

    const placementParam = urlParams.get('placement');
    if (placementParam) {
      localStorage.setItem('source_placement', placementParam.trim());
    }
  }, [location.search]);

  return null;
}

// App.js - PageTracker component
function PageTracker({ consent, ready }) {
  const location = useLocation();
  const trackedPages = useRef(new Set()); // ✅ Track what we've already tracked

  useEffect(() => {
    if (consent === 'none' || !ready) return;

    // ✅ Create unique key for this page view
    const pageKey = `${location.pathname}_${location.search}`;

    // ✅ Skip if we already tracked this exact page
    if (trackedPages.current.has(pageKey)) {
      return;
    }

    const source = getTrafficSource();

    if (consent === 'anonymous') {
      mixpanel.track("Page View", {
        path: location.pathname,
        title: document.title,
        traffic_source: source,
        user_type: 'anonymous',
        timestamp: new Date().toISOString(),
      });
    } else if (consent === 'full') {
      mixpanel.track("Page View", {
        path: location.pathname,
        title: document.title,
        traffic_source: source,
        user_type: 'identified',
        timestamp: new Date().toISOString(),
        referrer: document.referrer,
        user_agent: navigator.userAgent,
      });

      // Meta pixel PageView - fires once for the initial load and again on
      // every subsequent client-side route change (queues safely via the
      // fbq stub even before fbevents.js has finished loading).
      window.fbq?.('track', 'PageView');
    }

    // ✅ Mark as tracked
    trackedPages.current.add(pageKey);
  }, [consent, ready, location]);

  // ✅ Clear tracking when navigating to new page
  useEffect(() => {
    trackedPages.current.clear();
  }, [location.pathname]);

  return null;
}

function App() {
  const [consent, setConsent] = useState('anonymous'); // Start with anonymous tracking
  const [mixpanelReady, setMixpanelReady] = useState(false);

  useEffect(() => {
    if (consent === 'none') return;

    // Initialize Mixpanel for both anonymous and full consent
    if (MIXPANEL_TOKEN) {
      const mixpanelConfig = {
        track_pageview: false,  // we'll fire manually
        batch_requests: true,
        debug: false,
        persistence: consent === 'full' ? 'cookie' : 'localStorage',
        disable_persistence: false, // Always allow persistence for user tracking
        opt_out_tracking: false, // Explicitly enable tracking
      };

      mixpanel.init(MIXPANEL_TOKEN, mixpanelConfig);

      // Capture initial source and session data. Both are first-touch
      // (written to localStorage once and never overwritten - see
      // captureTrafficSource's/captureLandingPage's comments), so they're
      // still the visitor's original acquisition source/landing page even
      // after a full page reload, and stay the same value whether read here
      // for Mixpanel super properties or later at signup/checkout for
      // Postgres/Stripe - without that, the accounts_created/subscriptions
      // funnel stages (sourced from Postgres/Stripe) could disagree with
      // the page-view stages (sourced from Mixpanel) for a returning
      // visitor whose current session traffic source differs from the one
      // that originally brought them in.
      const source = captureTrafficSource();
      const landingPage = captureLandingPage();
      // AcquisitionParamsCapture (mounted below in the tree) already wrote any
      // ?src=/?platform=/?placement= params to localStorage before this effect
      // runs, since child effects fire before parent effects on mount.
      const sourceGroup = localStorage.getItem('source_group') || undefined;
      const sourcePlatform = localStorage.getItem('source_platform') || undefined;
      const sourcePlacement = localStorage.getItem('source_placement') || undefined;

      // Set super properties (properties included in all future events this session)
      if (consent === 'anonymous' || consent === 'full') {
        const superProperties = consent === 'full'
          ? {
              traffic_source: source,
              user_type: 'identified',
              session_start: new Date().toISOString(),
              initial_referrer: document.referrer,
              source_group: sourceGroup,
              source_platform: sourcePlatform,
              source_placement: sourcePlacement,
            }
          : {
              traffic_source: source,
              user_type: 'anonymous',
              session_start: new Date().toISOString(),
              source_group: sourceGroup,
              source_platform: sourcePlatform,
              source_placement: sourcePlacement,
            };

        // landingPage comes from captureLandingPage(), which is first-touch
        // (localStorage-backed), so it stays correct even after a full page
        // reload to /create-account (a plain <a href> in Home.js/
        // ThemeLandingPage.js) remounts App and re-runs this effect - unlike
        // recomputing straight from the current path, which would resolve to
        // null there. Still guard on truthiness: mixpanel.register()
        // overwrites whatever keys it's given, and a visitor whose very
        // first pageview isn't the homepage or a theme page has no
        // landingPage to set at all.
        if (landingPage) {
          superProperties.landing_page = landingPage;
        }

        mixpanel.register(superProperties);
      }

      // Persist first-touch attribution on the visitor's Mixpanel profile so
      // it's still queryable later even from events sent by the server (e.g.
      // a confirmed trial-start event fired from a webhook, long after the
      // original page view). set_once is a no-op if already set, so this is
      // safe to call every session.
      if (sourceGroup || sourcePlatform || sourcePlacement || landingPage || source) {
        mixpanel.people.set_once({
          first_landing_page: landingPage,
          first_traffic_source: source,
          first_source_group: sourceGroup,
          first_source_platform: sourcePlatform,
          first_source_placement: sourcePlacement,
        });
      }

      // Track initial app load
      mixpanel.track('App Load', {
        consent_level: consent,
        source: source,
      });

      setMixpanelReady(true);
    }

    if (consent === 'full') {

      // Facebook Pixel
      ; (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js"
      );
      window.fbq?.("init", FB_PIXEL_ID);
      // The initial PageView (and every subsequent SPA route change) is
      // fired from PageTracker below instead of here, so a client-side
      // navigation actually registers as a new pixel PageView - firing it
      // once here too would double-count the very first page load.

      // Google Tag / Ads
      const gtagScript = document.createElement("script");
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${AW_ID}`;
      gtagScript.async = true;
      gtagScript.onload = () => {
        window.dataLayer = window.dataLayer || [];
        function gtag() {
          window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
        gtag("js", new Date());
        gtag("config", AW_ID);
      };
      document.head.appendChild(gtagScript);
    }
  }, [consent]);


  useEffect(() => {
    // Handle consent level changes
    if (consent === 'full' && mixpanelReady) {
      // User upgraded from anonymous to full consent
      // DON'T reinitialize - just update properties and enable cookies
      if (MIXPANEL_TOKEN && typeof mixpanel !== 'undefined') {
        // Get the current distinct_id BEFORE any changes
        const currentDistinctId = mixpanel.get_distinct_id();
        
        // Update super properties to reflect the consent change
        const source = getTrafficSource();
        mixpanel.register({
          traffic_source: source,
          user_type: 'identified', // Changed from anonymous
          session_start: new Date().toISOString(),
          initial_referrer: document.referrer,
          consent_upgraded: true, // Flag that they upgraded consent
        });

        // Enable cookie persistence without losing the user ID
        mixpanel.set_config({
          persistence: 'cookie',
          disable_persistence: false
        });

        // Track the consent change (this will maintain the same distinct_id)
        mixpanel.track('Consent Upgraded', {
          previous_consent: 'anonymous',
          new_consent: 'full',
          source: source,
          preserved_user_id: currentDistinctId
        });
      }
    }
  }, [consent, mixpanelReady]); // Watch for changes in consent level

  return (
    <>
      {/* COOKIE CONSENT BANNER */}
      <CookieConsent
        location="bottom"
        buttonText="Accept"
        declineButtonText="Settings"
        enableDeclineButton
        cookieName="okgather_cookie_consent"
        style={{ background: "#222", color: "#fff" }}
        buttonStyle={{ background: "#fff", color: "#000", fontSize: "13px" }}
        declineButtonStyle={{
          background: "#555",
          color: "#fff",
          fontSize: "13px",
        }}
        expires={150}
        onAccept={() => setConsent('full')}
        onDecline={() => setConsent('anonymous')} // Keep anonymous even when declined
      >
        We use cookies for analytics and to help with advertising which you can find out more about in our policy.{" "}
        <a href="/privacy" style={{ color: "#ffa" }}>
          Learn more
        </a>
      </CookieConsent>

      <Router>
        <NCCParamCapture />
        <AcquisitionParamsCapture />
        <PageTracker consent={consent} ready={mixpanelReady} />
        <ScrollToTop />

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route path="/slots" element={<Slots />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/events" element={<EventSeries />} />
          <Route path="/gathering-matcher" element={<GatheringMatcher />} />
          {/* My Gatherings was removed - My Events replaced it. Keep the
              route registered (server-side SPA routing still serves this
              path) so old bookmarks/links land on the right page instead
              of a blank one. */}
          <Route path="/my-gatherings" element={<Navigate to="/my-events" replace />} />
          {/* Pathless layout route: renders TopNav once and keeps it mounted
              while navigating between these subscriber pages (see
              SubscriberLayout.js), instead of each page mounting its own
              TopNav and hiding it behind a full-page loading spinner on
              every click. */}
          <Route element={<SubscriberLayout />}>
            <Route path="edit-profile" element={<EditProfile />} />
            <Route path="my-events" element={<MyEvents />} />
            <Route path="connections" element={<Connections />} />
            <Route path="circles" element={<Circles />} />
            <Route path="stay-in-touch" element={<StayInTouch />} />
            <Route path="wishlist" element={<Wishlist />} />
          </Route>
          <Route path="/connect/:token" element={<ConnectViaLink />} />
          <Route path="/join/:token" element={<JoinEvent />} />
          <Route path="/wishlist/:token" element={<PublicWishlist />} />
          <Route path="/referrals" element={<ReferralDashboard />} />
          <Route path="/referral-terms" element={<ReferralTerms />} />
          <Route path="/invite" element={<ReferralLanding />} />
          <Route path="/signup" element={<SignupRedirect />} />
          <Route path="/find-gathering" element={<FindGathering />} />
          <Route path="/social" element={<SocialPage />} />
          <Route
            path="/waiting-list-confirmation"
            element={<WaitingListConfirmation />}
          />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin/panel" element={<AdminPanel />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/theme/:themeSlug" element={<ThemeLandingPage />} />
          <Route path="/local-events" element={<LocalEventsIndex />} />
          <Route path="/local-events/:slug" element={<LocalEventsPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;